import {
  findBenefitOption,
  usesBenefitSelectionWithPercent,
  type CapitalGlobalCoverageCatalogItem,
} from '@/features/quotes/schemas/capital-global/coverages-catalog';
import {
  capCapitalAndPercent,
  getCoverageCapitalsByCode,
  getEffectiveLimits,
  getPercentReferenceCapital,
} from '@/features/quotes/schemas/capital-global/coverage-limits.util';
import { formatCurrency } from '@/shared/utils/currencyFormatter';
import type { CoverageState } from '../sections/coverages-table.component';

function parsePercentToNumber(raw: string): number {
  return parseFloat(String(raw).replace(',', '.'));
}

export function propagateMorteCapitalChange(
  coverages: Record<string, CoverageState>,
  morteId: string,
  newMorteCapital: number,
  coverageCatalog: CapitalGlobalCoverageCatalogItem[],
  referenceCapital: number
): Record<string, CoverageState> {
  const updated = { ...coverages };
  const catalogById = new Map(coverageCatalog.map(c => [c.id, c]));
  const limitsCtx = {
    referenceCapital,
    morteCapital: newMorteCapital,
    morteId,
    coverageCapitalsByCode: getCoverageCapitalsByCode(
      coverageCatalog,
      coverages
    ),
  };
  const morteItem = coverageCatalog.find(item => item.id === morteId);
  if (morteItem) {
    limitsCtx.coverageCapitalsByCode.set(morteItem.code, newMorteCapital);
  }

  for (const [id, row] of Object.entries(updated)) {
    if (id === morteId || !row.enabled) continue;

    const catalogItem = catalogById.get(id);
    if (!catalogItem) continue;

    // Capital vindo da OPÇÃO de benefício é absoluto: recalcular pelo % da
    // nova Morte faria a linha divergir do nível escolhido no seletor (e o
    // attach mandaria um valor que não é nenhuma das opções). Nas diárias
    // (WITH_SI) o % é o input do valor da diária — re-deriva com a nova base.
    if (
      findBenefitOption(catalogItem, row.benefitCode) &&
      !usesBenefitSelectionWithPercent(catalogItem)
    ) {
      continue;
    }

    const pct = parsePercentToNumber(row.percent);
    if (!Number.isFinite(pct) || pct <= 0) continue;

    const percentRef = getPercentReferenceCapital(catalogItem, limitsCtx);
    const rawCapital = (percentRef * pct) / 100;
    const limits = getEffectiveLimits(catalogItem, limitsCtx);
    const { capital, percent } = capCapitalAndPercent({
      capital: rawCapital,
      percentRefCapital: percentRef,
      effectiveMax: limits.max,
      effectiveMin: limits.min,
    });

    updated[id] = {
      ...row,
      maxIndividualCapital: capital > 0 ? formatCurrency(capital) : '',
      percent,
    };
    limitsCtx.coverageCapitalsByCode.set(catalogItem.code, capital);
  }

  return updated;
}
